import React, { useState, useEffect } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Receipt, 
  TrendingDown, 
  TrendingUp, 
  Calendar,
  Landmark,
  Ticket,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  DollarSign
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const MemberLedger = () => {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    loadLedger();
  }, [selectedMonth]);

  const loadLedger = async () => {
    setLoading(true);
    try {
      const res = await userAPI.getLedger(selectedMonth);
      setLedger(res.data);
    } catch (error) {
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const formatMonthDisplay = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatCents = (cents) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  const getEntryIcon = (entryType, referenceType) => {
    if (referenceType === 'download') return <Ticket className="w-4 h-4" />;
    if (referenceType === 'payment') return <CreditCard className="w-4 h-4" />;
    if (entryType === 'credit') return <TrendingUp className="w-4 h-4" />;
    return <TrendingDown className="w-4 h-4" />;
  };

  const getEntryColor = (entryType) => {
    if (entryType === 'debit') return 'text-red-600';
    if (entryType === 'credit' || entryType === 'payment_recorded') return 'text-green-600';
    return 'text-blue-600';
  };

  const getEntryBadge = (entryType) => {
    if (entryType === 'debit') return <Badge className="bg-red-100 text-red-700">Debit</Badge>;
    if (entryType === 'credit') return <Badge className="bg-green-100 text-green-700">Credit</Badge>;
    if (entryType === 'payment_recorded') return <Badge className="bg-blue-100 text-blue-700">Payment</Badge>;
    if (entryType === 'adjustment') return <Badge className="bg-amber-100 text-amber-700">Adjustment</Badge>;
    return <Badge>{entryType}</Badge>;
  };

  if (loading) {
    return (
      <UserLayout title="Ledger">
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout title="Transaction Ledger">
      {/* Month Selector */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <h2 className="text-lg font-semibold text-slate-900">
          {formatMonthDisplay(selectedMonth)}
        </h2>
        <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-red-100 text-sm">Total Debits</p>
                <p className="text-3xl font-bold">{formatCents(ledger?.total_debits || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-green-100 text-sm">Total Credits</p>
                <p className="text-3xl font-bold">{formatCents(ledger?.total_credits || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-br ${ledger?.balance_due > 0 ? 'from-amber-500 to-amber-600' : 'from-slate-500 to-slate-600'} text-white`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-white/80 text-sm">Balance Due</p>
                <p className="text-3xl font-bold">{formatCents(ledger?.balance_due || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions List */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Transactions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!ledger?.entries || ledger.entries.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No transactions for this month</p>
            </div>
          ) : (
            <div className="divide-y">
              {ledger.entries.map((entry) => (
                <div key={entry.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`ledger-entry-${entry.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        entry.entry_type === 'debit' ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        {getEntryIcon(entry.entry_type, entry.reference_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          {getEntryBadge(entry.entry_type)}
                          <span className="text-xs text-slate-500">
                            {formatDateTime(entry.created_at)}
                          </span>
                        </div>
                        {entry.monument_name && (
                          <p className="text-sm font-medium text-slate-800 flex items-center gap-1 mt-1">
                            <Landmark className="w-3 h-3 text-slate-400" />
                            {entry.monument_name}
                          </p>
                        )}
                        {entry.slot_info && (
                          <p className="text-xs text-slate-500">{entry.slot_info}</p>
                        )}
                        {entry.note && (
                          <p className="text-sm text-slate-600 mt-1 italic">{entry.note}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${getEntryColor(entry.entry_type)}`}>
                        {entry.entry_type === 'debit' ? '-' : '+'}{formatCents(entry.amount_eur_cents)}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">{entry.reference_type.replace('_', ' ')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default MemberLedger;
